import { useRef } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "./SectionHeading";
import { useLiveProducts, useSiteText } from "@/lib/siteContent";
import { selectionIds } from "./Selection";
import { formatBRL } from "@/lib/format";

export default function NewArrivals() {
  const trackRef = useRef<HTMLDivElement>(null);
  const products = useLiveProducts();
  const items = products
    .filter((product) => product.isNew && !selectionIds.includes(product.id))
    .slice(0, 8);

  const eyebrow = useSiteText("newArrivals.eyebrow", "Chegou agora");
  const title = useSiteText("newArrivals.title", "Acabaram de chegar");
  const text = useSiteText(
    "newArrivals.text",
    "Novos modelos, novas cores e mais motivos para renovar os seus passos.",
  );

  const scrollByCard = (direction: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * 300, behavior: "smooth" });
  };

  return (
    <section id="novidades" className="w-full scroll-mt-24 bg-background-100" aria-label="Novidades Auréa">
      <div className="px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading
              eyebrow={eyebrow}
              title={title}
              text={text}
              align="left"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label="Ver novidades anteriores"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-arrow-left-line text-lg" />
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label="Ver próximas novidades"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-arrow-right-line text-lg" />
              </button>
            </div>
          </div>

          <Reveal className="mt-10">
            <div
              ref={trackRef}
              data-product-shop
              className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 md:gap-5"
            >
              {items.map((item) => (
                <article
                  key={item.id}
                  className="group w-[240px] shrink-0 snap-start overflow-hidden rounded-lg border border-background-200 bg-background-50 md:w-[272px]"
                >
                  <div className="relative h-[300px] w-full overflow-hidden bg-background-200 md:h-[340px]">
                    <Link to={`/produto/${item.id}`} className="block h-full w-full cursor-pointer">
                      <img
                        src={item.colors[0].image}
                        alt={`${item.name} — novidade em ${item.category} da Auréa`}
                        title={`${item.name} — novidade Auréa calçados e acessórios`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      />
                    </Link>
                    <span className="absolute left-3 top-3 rounded-full bg-secondary-500 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-foreground-950">
                      Novo
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg leading-snug text-foreground-900">
                      <Link
                        to={`/produto/${item.id}`}
                        className="cursor-pointer transition-colors hover:text-primary-600"
                      >
                        {item.name}
                      </Link>
                    </h3>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-primary-700">
                        {formatBRL(item.promoPrice ?? item.price)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {item.colors.map((color) => (
                          <span
                            key={color.name}
                            title={color.name}
                            className="h-4 w-4 rounded-full border border-background-300"
                            style={{ backgroundColor: color.hex }}
                          />
                        ))}
                      </div>
                    </div>
                    <Link
                      to={`/produto/${item.id}`}
                      className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      Ver
                      <i className="ri-arrow-right-line text-base" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 flex justify-center">
            <Link
              to="/novidades"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              Ver todas as novidades
              <i className="ri-arrow-right-line text-base" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}